/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import fetchMiniCartData from '@/actions/miniCart';
import { cartConstants } from '@/constants/cart';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { type OrderType } from '@/constants/orderTypes';
import { sessionStorage } from '@/utils/sessionStorage';
import { useAddToCart } from '@/features/orders/api/postAddToCart';
import { getFulfillmentOptionsByGroup } from '@/utils/itemHelpers/fulfillmentHelpers/getFulfillmentOptionsByGroup';
import { siteSections } from '@/utils/siteSections';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { useState, useCallback } from 'react';
import { useDispatch } from '@/hooks/redux/useDispatch';
import type {
  UseAddToCartWithMiniCartResult,
  AddToCartCallValues,
  AddToCartAnalyticsData,
} from '@/types/reduxStore/cartUpdate';
import { getErrorMessage } from '@/utils/errorsHandling';
import { useQueryClient } from '@tanstack/react-query';
import type { FulfillmentGroup } from '@/types/reduxStore/skuDetails';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import { routePaths } from '@/constants/routePaths';
import type { CustomLinkName } from '@/types/analytics';
import logger from '@/utils/logger';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { formatAcesYMME } from '@/utils/analytics/vehicleFormatter';
import { useHeaderData } from '@/features/header/api/getHeader';
import { isPdpPage } from '@/utils/analytics/isPdpPage';

type AddToCart = {
  eventType: string;
  productIdList: string[];
  productFulfillmentTypeList: string[];
  customLinkName: CustomLinkName;
  productBrandList: string[];
  productPriceList: string[];
  originalPartTypeIdList?: string[];
  productRepositoryIdList?: string[];
  productPartNumberList: string[];
  pageName: string;
  pageType: string;
  fulfillmentChange?: string;
  productCorePriceList?: string[];
  productAvailableQuantityList?: number[];
  productRecommendationTypeList?: string;
  productStrikeThroughPricing: string[];
  productFulfillmentOptionSeenByCustomer?: string[];
  pfmLocation?: string;
  productFindingMethod?: string;
  partGroupId?: string[];
  partTerminologyId?: string[];
  aces_ymme?: string[];
  productRecommendationInteraction?: string;
  productRecommendationType?: string;
  productQuantityList?: string[];
  productRequestedQuantityList?: string[];
  enhancedPDPContent?: boolean;
};

const trackAddToCartPDP = (
  valuesParm: AddToCartCallValues,
  analyticsData: AddToCartAnalyticsData,
  productAvailableQuantity?: number,
  acesEnabled?: boolean,
  acesYmme?: string[],
) => {
  const productFulfillmentTypeValue =
    typeof valuesParm.order_type === 'number'
      ? String(valuesParm.order_type)
      : valuesParm.orderType;

  const data: AddToCart = {
    eventType: 'addToCart',
    productIdList: [valuesParm.sku_id],
    productFulfillmentTypeList: [productFulfillmentTypeValue],
    customLinkName: analyticsData.customLinkName,
    productBrandList: [analyticsData.productBrand],
    productPriceList: [String(analyticsData.price)],
    productQuantityList: [String(valuesParm.quantity)],
    productRequestedQuantityList: [String(valuesParm.quantity)],
    productPartNumberList: [analyticsData.productPartNumber ?? ''],
    pageName: analyticsData.pageName,
    pageType: analyticsData.pageType,
    productRecommendationTypeList: analyticsData.productRecommendationTypeList,
    productStrikeThroughPricing: analyticsData.productStrikeThroughPricing,
    productFulfillmentOptionSeenByCustomer: [
      String(analyticsData.productFulfillmentOptionSeenByCustomer),
    ],
    ...(isPdpPage(analyticsData.pageType) && {
      enhancedPDPContent: sessionStorage.getItem('enhancedPDPContent') === 'true',
    }),
  };

  if (acesEnabled) {
    data.partGroupId = [analyticsData.partGroupId ?? ''];
    data.partTerminologyId = [analyticsData.partTerminologyId ?? ''];
    if (acesYmme && acesYmme.length) {
      data.aces_ymme = acesYmme;
    }
  } else {
    data.originalPartTypeIdList = [String(analyticsData.originalPartTypeId)];
    data.productRepositoryIdList = [analyticsData.productRepositoryId ?? ''];
  }

  if (analyticsData.pfmLocation) {
    data.pfmLocation = analyticsData.pfmLocation;
  }

  if (analyticsData.productFindingMethod) {
    data.productFindingMethod = analyticsData.productFindingMethod;
  }

  if (analyticsData.corePrice !== 0) {
    data.productCorePriceList = [String(analyticsData.corePrice)];
  }

  if (productAvailableQuantity !== undefined) {
    data.productAvailableQuantityList = [productAvailableQuantity];
  }

  if (analyticsData.productRecommendationInteraction) {
    data.productRecommendationInteraction = analyticsData.productRecommendationInteraction;
  }

  if (analyticsData.productRecommendationType) {
    data.productRecommendationType = analyticsData.productRecommendationType;
  }

  const isAffinityRecommendation = sessionStorage.getItem('pdpUtagLinkAffinity') === 'true';
  if (isAffinityRecommendation) {
    clickTrack({
      ...data,
      productRecommendationTypeList: ['Affinity'],
      productFindingMethod: 'Product Recommendation',
      pfmLocation: 'Affinity:catalog_product_detail',
      customLinkName: 'catalog_product_detail:Affinity:Cart Addition',
      siteSection: siteSections.siteSectionCatalog,
    });
    sessionStorage.removeItem('pdpUtagLinkAffinity');
  } else {
    clickTrack(data);
  }
};

export const useAddToCartWithMiniCart = (inDrawer = false): UseAddToCartWithMiniCartResult => {
  const { reset, status, mutate, error, data } = useAddToCart();
  const dispatch = useDispatch();
  const [cartSuccessNote, setCartSuccessNote] = useState(false);
  const [storeAvailability, setStoreAvailability] = useState(false);
  const [quantityInCart, setQuantityInCart] = useState(0);
  const [quantity, setQuantity] = useState('0');
  const [fulfillmentTypeId, setFulfillmentTypeId] = useState<OrderType | number | undefined>(
    undefined,
  );
  const [availabilityInfo, setAvailabilityInfo] = useState<FulfillmentGroup[]>([]);
  const queryClient = useQueryClient();
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const isCart = [routePaths.cart].includes(legacyRouter.asPath);
  const isRecommendationsLandingPage = legacyRouter.asPath.includes(
    routePaths.landingPageRecommendations,
  );
  const isCertonaAddToCartCTAEnabled = useCertonaAddToCartFlag();
  const miniCartEnabledForCertonaRecommendations =
    isCart || inDrawer || isRecommendationsLandingPage || isCertonaAddToCartCTAEnabled;
  const { acesEnabled } = useAcesFlags();
  const { data: headerData } = useHeaderData();

  const addToCartCall = (
    values: AddToCartCallValues,
    analyticsData: AddToCartAnalyticsData | null,
    catalogVehicleId: string | undefined,
    onSuccess?: () => void,
    onSettled?: () => void,
  ): void => {
    const orderType =
      typeof values.order_type === 'number'
        ? values.order_type
        : values.order_type === cartConstants.ONLINEORDER
          ? FULFILLMENT_METHODS.ONLINEORDER
          : FULFILLMENT_METHODS.STOREORDER;
    const orderTypev2 = values.orderType;
    const orderRequest = {
      skuId: values.sku_id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      productId: values.partGroupId ?? values.product_id,
      quantity: String(values.quantity),
      fulfillmentTypeId:
        miniCartEnabledForCertonaRecommendations && values.requestFromCertona
          ? orderType
          : orderTypev2,
      vehicleId: catalogVehicleId,
      storeNumber: values.store_number,
      configData: values.configData,
      searchedTerm:
        typeof legacyRouter.query.searchText === 'string'
          ? legacyRouter.query.searchText
          : typeof legacyRouter.query.filterByKeyWord === 'string'
            ? legacyRouter.query.filterByKeyWord
            : undefined,
      xFusionQueryId: values.xFusionQueryId,
      positionId: values.positionId,
      brandId: values.brandId,
      uniqueId: values.uniqueId,
    };

    mutate(orderRequest, {
      onError: () => {
        setQuantity(values.quantity);
        setFulfillmentTypeId(values.order_type);
      },
      onSuccess: async (data) => {
        const availabilityInfo = getFulfillmentOptionsByGroup(
          data.lineItemFulfillmentOptions.find((items) => items.skuId === values.sku_id)
            ?.fulfillmentOptions,
        );

        if (!values.isConfigureTechSkuToUseItem) {
          try {
            await dispatch(fetchMiniCartData(queryClient));
          } catch (error) {
            logger.error({
              message: 'Error fetching mini cart data',
              meta: {
                error,
              },
            });
          }
        }

        setAvailabilityInfo(availabilityInfo);
        setQuantity(values.quantity);
        setFulfillmentTypeId(values.order_type);
        setCartSuccessNote(true);

        if (onSuccess) {
          onSuccess();
        }

        if (values.isConfigureTechSkuToUseItem) {
          router.push('/cart?fromSku=true');
        } else {
          const productAvailableQuantity =
            availabilityInfo.find((option) => option.selected)?.maxAvailableQty ?? 0;

          if (analyticsData) {
            const acesYmme = acesEnabled ? formatAcesYMME(headerData?.vehicleMap) : undefined;
            trackAddToCartPDP(
              values,
              analyticsData,
              productAvailableQuantity,
              acesEnabled,
              acesYmme,
            );
          }
        }
      },
      onSettled: onSettled,
    });
  };

  const cartSuccess = data
    ? {
        ...data,
        availabilityInfo,
        orderType: fulfillmentTypeId,
        quantity: quantity,
      }
    : undefined;

  const productData = {
    readyStatus: status,
    err: error,
    cartSuccess,
    cartSuccessNote,
    storeAvailability,
    quantity,
    fulfillmentTypeId,
    quantityInCart,
  };
  const clearProductPageData = useCallback(() => {
    reset();
    setCartSuccessNote(false);
  }, [reset]);

  const changeFindProduct = () => {
    setStoreAvailability(true);
  };

  return {
    addToCartCall,
    emptyCartData: reset,
    clearProductPageData,
    changeFindProduct,
    addingToCartStatus: status,
    productData,
    err: error ? getErrorMessage(error) : null,
    setQuantityInCart,
    cartSuccess,
  };
};
